import {
    DEFAULT_EXPENSE_FIELD_ORDER,
    FIELD_IMPUTATION_LEAF,
    FIELD_IMPUTATION_LEVEL1,
} from '../components/client-form/expense-field-flags';

// Order of the expense review form's main fields (XPEN-3652), stored as
// `config.expenses_review.field_order`. These helpers only compare stored
// values with the 8 known slot ids, so a tampered value is ignored, never drawn.

const KNOWN_SLOTS = new Set(DEFAULT_EXPENSE_FIELD_ORDER);

const isIndexIn = (list, index) => Number.isInteger(index) && index >= 0 && index < list.length;

/**
 * Never let Sub-category sit above Category: Sub-category only appears once a
 * category is chosen, so above Category it would pop up above the field the
 * user just filled in. When it is above, it moves to just after Category;
 * otherwise the order is left as it is (other fields may sit between them).
 * @param {string[]} order complete order
 * @returns {string[]} new array; the input is not mutated
 */
export const keepLeafBelowCategory = (order) => {
    const levelIndex = order.indexOf(FIELD_IMPUTATION_LEVEL1);
    const leafIndex = order.indexOf(FIELD_IMPUTATION_LEAF);
    if (levelIndex === -1 || leafIndex === -1 || leafIndex > levelIndex) {
        return [...order];
    }
    const withoutLeaf = order.filter((slot) => slot !== FIELD_IMPUTATION_LEAF);
    const insertAt = withoutLeaf.indexOf(FIELD_IMPUTATION_LEVEL1) + 1;
    return [...withoutLeaf.slice(0, insertAt), FIELD_IMPUTATION_LEAF, ...withoutLeaf.slice(insertAt)];
};

/**
 * Build a complete, safe order from whatever is stored.
 * - not an array (absent, null, anything else) → the default order;
 * - keeps known slot ids only, first occurrence wins;
 * - appends missing slots in default order, so an empty list reads as the default;
 * - then keeps Sub-category below Category.
 * @param {unknown} savedOrder `config.expenses_review.field_order`
 * @returns {string[]} the 8 slot ids, each exactly once
 */
export const resolveExpenseFieldOrder = (savedOrder) => {
    if (!Array.isArray(savedOrder)) {
        return [...DEFAULT_EXPENSE_FIELD_ORDER];
    }
    const kept = savedOrder.filter((slot, index) => KNOWN_SLOTS.has(slot) && savedOrder.indexOf(slot) === index);
    const missing = DEFAULT_EXPENSE_FIELD_ORDER.filter((slot) => !kept.includes(slot));
    return keepLeafBelowCategory([...kept, ...missing]);
};

/**
 * Apply one drag to a complete order: move the slot at `fromIndex` to
 * `toIndex`, then keep Sub-category below Category.
 * @param {string[]} order complete order
 * @param {number} fromIndex
 * @param {number} toIndex
 * @returns {string[]} new array; unchanged order for an out-of-range index
 */
export const moveExpenseField = (order, fromIndex, toIndex) => {
    if (!isIndexIn(order, fromIndex) || !isIndexIn(order, toIndex)) {
        return [...order];
    }
    const next = [...order];
    const [moved] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, moved);
    return keepLeafBelowCategory(next);
};

/**
 * Apply one drag made on a filtered view of the order. The admin list draws
 * only the slots present in the client's template, and react-beautiful-dnd
 * reports indexes in that drawn list. Passing them straight to
 * moveExpenseField would move the wrong field, so they are mapped back to the
 * full order first. A slot that is not drawn keeps its place among the rows it
 * is not moved across.
 * @param {string[]} order complete order
 * @param {string[]} visible the drawn slots, in order
 * @param {number} fromIndex index in `visible`
 * @param {number} toIndex index in `visible`
 * @returns {string[]} new complete order
 */
export const moveVisibleExpenseField = (order, visible, fromIndex, toIndex) => {
    if (!isIndexIn(visible, fromIndex) || !isIndexIn(visible, toIndex)) {
        return [...order];
    }
    return moveExpenseField(order, order.indexOf(visible[fromIndex]), order.indexOf(visible[toIndex]));
};
